<template>
  <HeaderBar :modifiers="['transparent', 'form']">
    <a @click="$router.back()" class="button">
      <UseSvg name="arrow_left_16" class="icon button__icon" />
      <span class="button__label">{{ $t('nav.profile') }}</span>
    </a>
  </HeaderBar>
  <div class="form-layout">
    <div class="form-layout__heading">
      <h2 class="form-layout__title typo-small-title">
        {{ $t('profile.change_password') }}
      </h2>
      <p class="form-layout__description typo-small-title">
        {{ $t('profile.password_description') }}
      </p>
    </div>
    <div class="form-layout__content">
      <form>
        <!-- Current password field -->
        <FormRow
          :label="$t('profile.current_password')"
          :modifiers="['no-padding']"
          labelFor="current_password"
          :errors="errors && errors.user.current_password"
        >
          <div class="form-row__input form-row__input--no-peek">
            <div :class="{ 'input': true, 'input--error': errors && errors.user.current_password }">
              <input
                type="password"
                id="current_password"
                v-model="currentPassword"
                autocomplete="off"
                @input="validateInput('current_password')"
              />
            </div>
          </div>
        </FormRow>

        <!-- New password field -->
        <FormRow
          :label="$t('profile.new_password')"
          :modifiers="['no-padding']"
          labelFor="password"
          :errors="errors && errors.user.password"
        >
          <div class="form-row__input form-row__input--no-peek">
            <div :class="{ 'input': true, 'input--error': errors && errors.user.password }">
              <input
                type="password"
                id="password"
                v-model="newPassword"
                autocomplete="off"
                @input="validateInput('password')"
              />
            </div>
          </div>
        </FormRow>

        <!-- confirm new password field -->
        <FormRow
          :label="$t('profile.confirm_new_password')"
          :modifiers="['no-padding']"
          labelFor="password_confirm"
          :errors="errors && errors.user.password_confirmation"
        >
          <div class="form-row__input form-row__input--no-peek">
            <div :class="{ 'input': true, 'input--error': errors && errors.user.password_confirmation }">
              <input
                type="password"
                id="password_confirm"
                v-model="confirmPassword"
                :errors="errors && errors"
                @input="newPasswordConfirmation"
                autocomplete="off"
              />
            </div>
          </div>
        </FormRow>
      </form>

      <!-- Update action -->
      <div class="form-layout__actions">
        <FormSubmitButton
          @click="submitUpdatePassword"
          :buttonLabel="$t('profile.update_password')"
          :isSaving="saving"
          :disabled="submitDisabled"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import useAjax from '@/functions/use_ajax';
import useEmitter from '@/functions/use_emitter';
import I18n from 'i18n-js';

// Components
import HeaderBar from '@/components/generic/header_bar.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import FormRow from '@/components/form/row.vue';
import FormSubmitButton from '@/components/form/form_submit_button.vue';

const router = useRouter();
const ajax = useAjax();
const emitter = useEmitter();
const currentPassword = ref(null);
const newPassword = ref(null);
const confirmPassword = ref(null);
const errors = ref(null);
const saving = ref(false);

/**
 * check inputs and errors for submit button disabled state
 */
const submitDisabled = computed(() => {
  if (currentPassword.value && newPassword.value && confirmPassword.value && !errors.value) {
    return false;
  }

  return true;
});

/**
 * Check new password and confirm password
 */
function newPasswordConfirmation() {
  if (newPassword.value !== confirmPassword.value) {
    errors.value = {
      'user': {
        'password_confirmation': [I18n.t('profile.password_doesnt_match')]
        }
      };
  } else {
    errors.value = null;
  }
}

/**
 * removes errors when starting to type again
 */
function validateInput(field) {
  if (errors.value) {
    errors.value.user[field] = null;
  }
}

/**
 * Submit update password
 */
async function submitUpdatePassword() {
  saving.value = true;

  let { ok, data } = await ajax('/users/change_password', {
    method: 'PATCH',
    body: {
      user: {
        current_password: currentPassword.value,
        password: newPassword.value,
        password_confirmation: confirmPassword.value,
      },
    },
  });

  if (ok) {
    emitter.emit('toast', { status: 'success', message: I18n.t('controllers.users.update.notice') });
    router.back();
  } else {
    errors.value = data;

    if (data.user.current_password) {
      newPassword.value = null;
      confirmPassword.value = null;
    } else if (data.user.password) {
      confirmPassword.value = null;
    }
  }

  saving.value = false;
}
</script>
